<template>
  <li class="content-row">
    <div class="content-row__main-container" @click="goToContent">
      <div
        class="content-row__main-container__tile-img"
        :style="getBackgroundImage"
      >
        <PlayIcon v-if="showPlayIcon" />
        <SkillDiveWhiteLogo v-if="isLabExperience" />
        <div
          v-if="progress"
          class="content-row__main-container__tile-img__progress-bar-wrapper"
        >
          <PreviewProgressBar
            :value="progress"
            size="small"
          ></PreviewProgressBar>
        </div>
      </div>
      <div class="content-row__main-container__info">
        <div
          v-if="!isLabExperience || isLabRunning"
          class="content-row__main-container__info__top"
        >
          <span
            :class="[
              'content-row__main-container__info__top__content-type',
              isLabRunning &&
                'content-row__main-container__info__top__content-type--lab-running',
            ]"
          >
            {{
              contentType === 'learning-path' ? 'learning path' : contentType
            }}
          </span>
          <div
            v-if="isLabRunning"
            class="content-row__main-container__info__top__lab-running"
          >
            <div
              class="content-row__main-container__info__top__lab-running__pill-wrapper"
            >
              <RunningPill />
            </div>
            <span
              class="content-row__main-container__info__top__lab-running__label-pointer"
            >
              Running
            </span>
          </div>
        </div>
        <div
          :class="[
            'content-row__main-container__info__title',
            isLabExperience &&
              'content-row__main-container__info-labexperience-title',
          ]"
        >
          <span>{{ title }}</span>
          <LockIcon
            v-if="isLabExperience && lockedContent(content)"
            v-tooltip="{
              content: 'This lab is Locked.</br>GetAccess.',
              placement: 'right-center',
            }"
            class="content-row__main-container__info-lock"
          />
        </div>
        <div class="content-row__main-container__info__bottom">
          <CertificatePointer
            v-if="certificate"
            :certificate="certificate"
            :color="learningAreaColor"
          />
          <LearningAreaLabel
            :learningArea="learningAreaName"
            :showIcon="false"
            :dotIcon="true"
          />
          <span v-if="showSkillCheckIcon" class="content-row__skill-check">
            <SkillCheckIcon class="content-row__skill-check-icon" />
            Skill Check
          </span>
          <span v-if="difficulty">
            {{ difficulty }}
          </span>
          <span v-if="duration">
            {{ duration }}
          </span>
          <span v-if="activeTimeAgo">
            {{ activeTimeAgo }}
          </span>
          <span v-if="content.cpe_credits" class="content-row__cpe-credits">
            {{ `${content.cpe_credits} CPE Credits` }}
            <InfoIcon
              v-if="isAuthenticated"
              v-tooltip="{
                content: cpeTooltipText,
                placement: 'right',
                classes: ['tooltip--text-center'],
              }"
              size="14"
            />
          </span>
          <span v-if="contentType !== 'lab'">
            {{ instructorLabel }}
          </span>
          <span v-if="contentType !== 'lab'">
            {{ vendorName }}
          </span>
          <span
            v-if="isCompleted"
            class="content-row__main-container__info__bottom-completed"
          >
            <CheckIcon /> Completed
          </span>
        </div>
        <div
          v-if="isPlaylistAssigned"
          class="content-row__main-container__assigned-wrapper"
        >
          <CheckIcon
            class="content-row__main-container__assigned-icon"
          ></CheckIcon>
          <span class="content-row__main-container__assigned-label"
            >Assigned to you</span
          >
        </div>
        <div class="content-row__main-container__info__info-btn-wrapper">
          <button
            v-tooltip="{
              content: 'More information',
              placement: 'top-center',
            }"
            @click.stop="toggleExpand"
          >
            <RotatingContainer :play="isContentLoading">
              <LoaderIcon v-if="isContentLoading" />
              <InfoIcon v-else />
            </RotatingContainer>
          </button>
        </div>
        <div
          v-if="content.published_date && showAddedDaysAgo"
          class="content-row__main-container__info__added-date-wrapper"
        >
          <AddedDaysAgo :label="addedInDaysText" />
        </div>
      </div>
    </div>
    <transition name="content-row__details-container--expand">
      <div v-if="isExpanded" class="content-row__details-container">
        <div class="content-row__details-container__left">
          <div
            v-if="descriptionContent"
            class="content-row__details-container__left__description"
            v-html="descriptionContent"
          ></div>
          <div
            v-if="certificate"
            class="content-row__details-container__left__certificate"
          >
            <img
              class="content-row__details-container__left__certificate__badge"
              :src="certificate.logo_image_url"
              alt="CERTIFICATE_BADGE"
            />
            <div
              class="content-row__details-container__left__certificate__title"
            >
              <span
                class="content-row__details-container__left__certificate__title__label"
              >
                This learning path prepares you for a certification:
              </span>
              <span
                class="content-row__details-container__left__certificate__title__name"
              >
                {{ certificate.name }}
              </span>
            </div>
          </div>
          <ParentContentTags
            class="content-row__details-container__left__parent_content_tags"
            :isLabExperience="isLabExperience"
            :contentType="contentType"
            :contents="parentContents"
          />
        </div>
        <div v-if="summary" class="content-row__details-container__right">
          <SummaryTile
            :learningAreaColor="learningAreaColor"
            :certificate="certificate"
            :summary="summary"
          />
        </div>
      </div>
    </transition>
  </li>
</template>

<script>
import { mapState } from 'vuex'
import {
  PlayIcon,
  InfoIcon,
  LoaderIcon,
  LockIcon,
  CheckIcon,
} from 'vue-feather-icons'
import SkillDiveWhiteLogo from '@/assets/images/lab-experience/skilldive-logo-white.svg'
import SkillCheckIcon from '@/assets/icons/skill_check_icon.svg'
import {
  PreviewProgressBar,
  LearningAreaLabel,
  CertificatePointer,
  RunningPill,
} from '@/components/properties'
import {
  getEntities,
  getLearningAreaColor,
  getLearningAreaName,
} from '@/helpers'
import { differenceInDays, formatDistanceToNow } from 'date-fns'
import CONSTANTS from '@/constants'
import RotatingContainer from '@/components/shared/RotatingContainer.vue'
import SummaryTile from '@/components/learning-path/overview/SummaryTile.vue'
import AddedDaysAgo from '@/components/search/content-rows/AddedDaysAgo.vue'
import ParentContentTags from '@/components/search/content-rows/ParentContentTags.vue'
import { collectionCommons, licenseAddonCommons } from '@/helpers/mixins'
import { isEmpty } from 'lodash'

export default {
  name: 'ContentRow',
  components: {
    PlayIcon,
    InfoIcon,
    LoaderIcon,
    LockIcon,
    SkillDiveWhiteLogo,
    AddedDaysAgo,
    ParentContentTags,
    RunningPill,
    SummaryTile,
    PreviewProgressBar,
    LearningAreaLabel,
    RotatingContainer,
    CertificatePointer,
    CheckIcon,
    SkillCheckIcon,
  },
  mixins: [collectionCommons, licenseAddonCommons],
  props: {
    content: {
      type: Object,
      required: false,
      default: null,
    },
    searchIndex: {
      type: Object,
      required: false,
      default: null,
    },
    openInNewTab: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignedPlaylist: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data: () => {
    return {
      isExpanded: false,
      parentContents: [],
      isLoading: false,
      CONTENT_TYPE_TITLES: CONSTANTS.CONTENT_TYPE_TITLES,
      descriptionContent: '',
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      activeCourses: state => state.courses.activeCourses,
      currentLab: state => state.labs.currentLab,
      runningLabSession: state => state.labs.runningLabSession,
      labUserStatuses: state => state.labs.labUserStatuses,
    }),
    contentId() {
      return this.content.objectID
    },
    isCompleted() {
      const {
        CONTENT_TYPES: { LAB },
        CONTENT_USER_STATUS: { FINISHED },
      } = CONSTANTS
      const { contentId, labUserStatuses, contentType } = this

      if (contentType !== LAB) return false

      return labUserStatuses[contentId]?.user_lab_status === FINISHED
    },
    isContentLoading() {
      return this.isLoading || this.skillDiveLabsLoading
    },
    contentType() {
      return this.content.content_type
    },
    getBackgroundImage() {
      const linearGradient = `linear-gradient(${this.gradient})`
      if (CONSTANTS.CONTENT_TYPES.LAB === this.contentType)
        return `background: ${linearGradient}`
      return `background-image: url(${this.tileImg}), ${linearGradient}`
    },
    tileImg() {
      const { objectID } = this.content
      let img

      switch (this.contentType) {
        case 'course':
          img = this.$getCourseBackground(objectID, 'xxs')
          break
        case 'learning-path':
          img = this.$getLearningPathBackground(objectID)
          break
      }

      return img
    },
    gradient() {
      if (this.isLabExperience) return '127deg, #5C16C6 18.91%, #44D3C9 108.52%'
      return this.$getGradient()
    },
    title() {
      return this.content.name
    },
    learningAreaName() {
      return getLearningAreaName(this.content)
    },
    learningAreaColor() {
      return getLearningAreaColor(this.content)
    },
    certificate() {
      const { certifications } = this.content

      return certifications ? certifications[0] : null
    },
    cpeTooltipText() {
      return CONSTANTS.CPE.CPE_PROGRAM_CONTENT_TOOLTIP_MSG
    },
    difficulty() {
      return this.content.difficulty
    },
    duration() {
      return this.$duration.durationFromSecs(this.content.duration, 'minutes')
    },
    activeCourse() {
      if (this.contentType !== 'course') return null

      return this.activeCourses.find(ac => ac.id === this.content.objectID)
    },
    progress() {
      const { contentType, activeCourse } = this
      let progress = 0

      switch (contentType) {
        case 'course':
          if (activeCourse)
            progress = Math.floor(activeCourse.user_status.progress * 100)
      }

      return progress
    },
    activeTimeAgo() {
      const { activeCourse } = this

      if (activeCourse?.user_status?.last_activity_date) {
        const timeAgo = formatDistanceToNow(
          new Date(activeCourse.user_status.last_activity_date),
          {
            addSuffix: true,
          }
        )

        return `Active ${timeAgo}`
      }

      return null
    },
    instructorName() {
      return getEntities.getInstructorName(this.content)
    },
    amountOfInstructors() {
      return getEntities.getAmountOfInstructors(this.content)
    },
    instructorLabel() {
      return this.amountOfInstructors > 2
        ? `${this.instructorName} +${this.amountOfInstructors - 1}`
        : this.instructorName
    },
    vendorName() {
      return getEntities.getVendorName(this.content)
    },
    isLabRunning() {
      const { contentType, runningLabSession } = this

      return (
        contentType === 'lab' &&
        runningLabSession?.lab_id === this.content.objectID
      )
    },
    parentIds() {
      const { metadata } = this.content
      const parentTypes = ['courses', 'learning_paths', 'collections']
      const parentIds = []

      if (metadata) {
        parentTypes.forEach(parentType => {
          metadata[parentType]?.forEach(id => {
            parentIds.push(id)
          })
        })
      }

      return parentIds
    },
    publishedDateInMiliseconds() {
      return this.content.published_date * 1000
    },
    showAddedDaysAgo() {
      return (
        differenceInDays(
          new Date(),
          new Date(this.publishedDateInMiliseconds)
        ) <= 7
      )
    },
    addedInDaysText() {
      const timeAgo = formatDistanceToNow(
        new Date(this.publishedDateInMiliseconds),
        {
          addSuffix: true,
        }
      )

      return `Added ${timeAgo}`
    },
    summary() {
      const { content, duration, instructorName, certificate } = this
      const { summary, difficulty } = content

      return !this.isLabExperience && summary
        ? Object.fromEntries(
            Object.entries({
              DIFFICULTY: difficulty,
              DURATION: duration,
              INSTRUCTOR: instructorName,
              SECTIONS: summary.total_groups,
              COURSES: summary.total_courses,
              VIDEOS: summary.total_videos,
              QUIZZES: summary.total_quizzes,
              LABS: summary.total_labs,
              EXERCISES: summary.total_exercises,
              SLIDES: summary.total_iframes,
              CERTIFICATION: certificate?.short_name,
            }).filter(([_, v]) => v === 0 || !!v)
          )
        : false
    },
    showPlayIcon() {
      return !this.isLabExperience
    },
    isLabExperience() {
      return this.content?.is_lab_experience
    },
    isPlaylistAssigned() {
      return !isEmpty(this.assignedPlaylist)
    },
    showSkillCheckIcon() {
      return (
        this.content.has_skill_check_labs &&
        (this.contentType === 'learning-path' || this.contentType === 'course')
      )
    },
  },
  created() {
    if (this.content?.is_lab_experience) {
      this.labContent = this.content
      this.setSkillDiveTags()
    }
  },
  methods: {
    async toggleExpand() {
      const { isExpanded } = this

      if (isExpanded) {
        this.isExpanded = false
      } else {
        if (this.searchIndex) await this.loadParentContents()
        if (this.content.content_type === 'lab' && !this.content.description) {
          this.isLoading = true
          // this piece of code depends on https://github.com/Internetworkexpert/my.ine.com/pull/1989
          await this.$store.dispatch('labs/getLabById', {
            id: this.content.id || this.content.objectID,
            parentId: this.content.parent_id,
            parentType: this.content.parent_type,
          })
          this.isLoading = false
        }
        this.getDescription()
        this.isExpanded = true
      }
    },
    async loadParentContents() {
      const { parentIds, parentContents } = this

      if (parentIds.length && !parentContents.length) {
        try {
          this.isLoading = true

          const { results } = await this.searchIndex.algoliaIndex.getObjects(
            parentIds
          )

          this.parentContents = this.isLabExperience
            ? results
                .filter(Boolean)
                .filter(r => r.content_type === 'collection')
            : results.filter(Boolean)
        } catch ({ message }) {
          this.$store.dispatch('app/setError', message)
        } finally {
          this.isLoading = false
        }
      }
      return this.parentContents
    },
    async goToContent() {
      const { contentType, content } = this
      const { CONTENT_TYPES } = CONSTANTS

      if (this.isLabExperience && this.lockedContent(content)) {
        this.getAccessToLockedSDLab()
      } else {
        if (contentType === CONTENT_TYPES.VIDEO) {
          await this.loadParentContents()

          return this.$emit('open-video', {
            video: content,
            parents: this.parentContents,
          })
        } else if (contentType === CONTENT_TYPES.LAB) {
          if (this.isAuthenticated) {
            if (this.searchIndex) await this.loadParentContents()

            const parent = this.parentContents[0]

            if (parent) {
              const { objectID: parent_id, content_type: parent_type } = parent
              const contentLink = this.$buildContentLink(
                contentType,
                { ...content, parent_id, parent_type },
                parent
              )
              this.doLink(contentLink)
            } else {
              this.doLink(this.$buildContentLink(contentType, content))
            }
          } else {
            this.$store.dispatch(
              'app/setError',
              'Please login to open this lab'
            )
          }
        } else {
          this.doLink(this.$buildContentLink(contentType, content))
        }
      }
    },
    getDescription() {
      this.descriptionContent =
        this.content.content_type === 'lab' && !this.content.description
          ? this.currentLab.description_html
          : this.content.description
    },
    doLink(linkData) {
      if (!this.openInNewTab) return this.$router.push(linkData)
      const routeUrl = this.$router.resolve(linkData)?.href
      window.open(routeUrl, '_blank')
    },
  },
}
</script>

<style scoped lang="scss">
.content-row {
  $this: &;
  border-radius: 10px;
  overflow: hidden;
  color: var(--white);
  font-family: var(--font-book);

  &__main-container {
    display: flex;
    background: var(--glass-mid-gray);
    cursor: pointer;

    &:hover {
      background: var(--glass-extra-light);
    }
    &:active {
      background: var(--glass-dark);
    }

    &__tile-img {
      display: flex;
      position: relative;
      justify-content: center;
      align-items: center;
      width: 110px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;

      @include mobile {
        width: 133px;
      }

      &__badge {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        padding: 5px 0px;
        width: 100%;
        &--lab {
          background: var(--primary-ine-gradient);
        }
        span {
          font-family: var(--font-book);
          font-weight: 450;
          font-size: rem(12px);
          line-height: rem(14px);
        }
      }
      &__progress-bar-wrapper {
        position: absolute;
        bottom: 10px;
        width: 85%;
      }
    }

    &__info {
      display: grid;
      grid-template-columns: auto min-content min-content;
      grid-template-rows: min-content min-content;
      grid-auto-rows: auto min-content;
      gap: 0 5px;
      position: relative;
      flex: 1;
      padding: 10px;

      @include mobile {
        padding: 20px;
      }

      &__top {
        display: flex;
        flex-flow: column;
        font-family: var(--font-book);
        font-weight: 450;
        font-size: rem(10px);
        line-height: rem(12px);
        grid-column: 1 / 3;
        grid-row: 1;

        @include mobile {
          grid-column: 1;
          grid-row: 1;
          flex-flow: row;
          align-items: center;
          gap: 5px;
          height: 14px;
          font-size: rem(12px);
          line-height: rem(14px);
        }
        &__content-type {
          text-transform: uppercase;
          &--lab-running {
            display: none;
          }
        }
        &__lab-running {
          &__pill-wrapper {
            display: none;
            @include mobile {
              display: initial;
            }
          }
          &__label-pointer {
            display: flex;
            &::after {
              content: '';
              width: 13px;
              height: 13px;
              border-radius: 50%;
              background-color: var(--positive-light);
              box-shadow: var(--positive-light-shadow);
              margin-left: 5px;
            }

            @include mobile {
              display: none;
            }
          }
        }
      }
      &__title {
        margin-top: 10px;
        overflow: hidden;
        font-family: var(--font-bold);
        font-style: normal;
        font-weight: 700;
        font-size: rem(12px);
        line-height: rem(14px);
        grid-column: 1 / 3;
        grid-row: 2;

        @include mobile {
          grid-column: 1;
          grid-row: 2/3;
          height: fit-content;
          font-family: var(--font-medium);
          font-weight: 500;
          font-size: rem(16px);
          line-height: rem(23px);
        }
        @include desktop {
          height: 23px;
        }
      }
      &__bottom {
        display: flex;
        align-items: center;
        column-gap: 10px;
        height: 40px;
        font-family: var(--font-book);
        font-style: normal;
        font-weight: 450;
        font-size: rem(10px);
        line-height: rem(12px);
        margin-top: 5px;
        grid-column: 1;
        grid-row: 4;
        overflow: hidden;
        margin: auto 0 0 0;
        @include mobile {
          grid-row: 3/3;
          height: 20px;
          font-size: rem(12px);
          line-height: rem(14px);
          margin: 2px 0 0 0;
        }

        > * {
          white-space: nowrap;
          text-transform: capitalize;
          border-right: 1px solid var(--perfect-gray);
          padding-right: 10px;
          height: 12px;

          &:last-child {
            border-right: none;
          }

          @include mobile {
            height: 14px;
          }
        }

        #{$this}__cpe-credits {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        &-completed {
          display: flex;
          gap: 5px;
          align-items: center;
          color: var(--positive-light);

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
      &__info-btn-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        grid-column: 2;
        grid-row: 4;
        margin-top: auto;
        @include mobile {
          margin-top: 0;
          grid-column: 3;
          grid-row: 1 / span 3;
        }

        button {
          display: flex;
          justify-content: center;
          align-items: center;
          background: transparent;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 8px;
          cursor: pointer;

          &:hover {
            background: var(--white-10);
          }

          svg {
            color: var(--white);
            width: 16px;
            height: 16px;
          }
        }
      }
      &__added-date-wrapper {
        position: absolute;
        top: 0;
        right: 0;
      }

      &-lock {
        padding: 12px;
        width: 40px;
        height: 40px;
      }

      &-labexperience-title {
        display: flex;
        align-items: center;
      }
    }
    &__assigned {
      &-wrapper {
        width: 159px;
        grid-column: 1;
        grid-row: 3/4;
        height: 12px;
        display: flex;
        align-items: center;
        gap: 2px;
        font-size: rem(10);
        margin: 5px 0 0 0;
        color: var(--caribbean-green);
        @include mobile {
          gap: 10px;
          height: 20px;
          grid-column: 2;
          grid-row: 1 / span 3;
          font-size: rem(14);
          padding: 8px 16px;
          margin: 0;
          height: 100%;
        }
      }
      &-icon {
        width: 16px;
      }
    }
  }

  &__details-container {
    display: flex;
    flex-flow: column;
    gap: 20px;
    background: var(--glass-mid-gray);
    padding: 20px;
    opacity: 1;

    @include desktop {
      flex-flow: row;
    }
    &--expand {
      &-enter-active,
      &-leave-active {
        transition: all 0.2s ease-in-out;
      }
      &-enter,
      &-leave-to {
        padding: 0 20px;
        max-height: 0px;
        opacity: 0;
      }
    }

    &__left {
      display: flex;
      flex: 1;
      flex-flow: column;
      gap: 20px;
      color: var(--athens-gray);

      font-family: var(--font-book);
      font-style: normal;
      font-weight: 450;
      font-size: rem(10px);
      line-height: rem(12px);

      @include mobile {
        font-size: rem(14px);
        line-height: rem(20px);
      }
      &__description {
        ::v-deep a {
          color: var(--white);
          text-decoration: underline;
          &:visited {
            color: var(--white);
          }
        }
        ::v-deep img {
          width: 100%;
        }
      }
      &__certificate {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 8px 0;

        &__badge {
          max-width: 120px;
          max-height: 120px;
          background-size: contain;
        }

        &__title {
          &__label {
            font-family: var(--font-medium);
            font-style: normal;
            font-weight: 500;
          }
          &__name {
            display: block;
          }
        }
      }
    }

    &__right {
      @include desktop {
        width: 300px;
      }
    }
  }
  &__skill-check {
    display: flex;
    align-items: center;

    &-icon {
      width: 15px;
      height: 15px;
      margin-right: 5px;
    }
  }
}
</style>
